import React, { useEffect, useRef, useState } from 'react';

import researchService from '../../services/researchService';
import DefinitionSelector from './DefinitionSelector';
import SheetTabBar from './SheetTabBar';
import TagChip from './TagChip';
import EditableLabel from './EditableLabel';
import Badge from './Badge';
import RowVisibilityControls from './RowVisibilityControls';
import { slugifySheetKey } from '../../utils/slugifySheetKey';

const DEFAULT_SHEETS = [
  { key: 'IS', name: 'Income Statement' },
  { key: 'BS', name: 'Balance Sheet' },
  { key: 'CFS', name: 'Cash Flow Statement' }
];

/**
 * ModelConfigTab — visualise and edit how the financial model spreadsheet is built.
 *
 * Displays one tab per sheet (IS / BS / CFS).  Each row shows:
 *   • Row label  (click to edit)
 *   • Type badge  (metric / formula / combined / header)
 *   • SEC XBRL tags used to populate the row  (click to add/remove)
 *   • Negate toggle  (flips the sign of SEC raw values for outflow items)
 *
 * Edits are applied locally and saved to DynamoDB via PUT /model-config.
 */



/** Searchable dropdown for picking a known XBRL tag name. */
function TagPicker({ knownTags, excludeTags, onPick, onCancel }) {
  const [query, setQuery] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const matches = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const candidates = knownTags.filter(t => !excludeTags.includes(t));
    if (!q) return candidates.slice(0, 80);
    return candidates.filter(t => t.toLowerCase().includes(q)).slice(0, 80);
  }, [query, knownTags, excludeTags]);

  useEffect(() => { setHighlightIdx(0); }, [matches]);

  const pick = (tag) => { if (tag) onPick(tag); };

  const scrollIntoView = (idx) => {
    const item = listRef.current?.children[idx];
    if (item) item.scrollIntoView({ block: 'nearest' });
  };

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => { const n = Math.min(i + 1, matches.length - 1); scrollIntoView(n); return n; });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => { const n = Math.max(i - 1, 0); scrollIntoView(n); return n; });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (matches[highlightIdx]) pick(matches[highlightIdx]);
      else if (query.trim()) pick(query.trim()); // allow freehand
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        <input
          ref={inputRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKey}
          placeholder={knownTags.length ? 'Search XBRL tag…' : 'XBRL tag name…'}
          style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #0d6efd', borderRadius: 4, width: 220, fontFamily: 'monospace' }}
        />
        <button className="btn btn-secondary btn-sm py-0 px-2" style={{ fontSize: 11 }} onClick={onCancel}>✕</button>
      </div>
      {matches.length > 0 && (
        <ul
          ref={listRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 999,
            background: '#fff',
            border: '1px solid #ced4da',
            borderRadius: 4,
            margin: 0,
            padding: 0,
            listStyle: 'none',
            maxHeight: 220,
            overflowY: 'auto',
            minWidth: 260,
            boxShadow: '0 4px 12px rgba(0,0,0,.12)',
            fontSize: 11,
            fontFamily: 'monospace',
          }}
        >
          {matches.map((t, i) => (
            <li
              key={t}
              onMouseDown={() => pick(t)}
              onMouseEnter={() => setHighlightIdx(i)}
              style={{
                padding: '4px 10px',
                cursor: 'pointer',
                backgroundColor: i === highlightIdx ? '#0d6efd' : 'transparent',
                color: i === highlightIdx ? '#fff' : '#212529',
                whiteSpace: 'nowrap',
              }}
            >
              {t}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const CANNED_FORMULAS = [
  {
    id: 'sum2',
    label: 'Sum  (A + B)',
    params: [{ k: 'a', lbl: 'Row A' }, { k: 'b', lbl: 'Row B' }],
    build: p => `{${p.a}} + {${p.b}}`,
  },
  {
    id: 'sum3',
    label: 'Sum  (A + B + C)',
    params: [{ k: 'a', lbl: 'Row A' }, { k: 'b', lbl: 'Row B' }, { k: 'c', lbl: 'Row C' }],
    build: p => `{${p.a}} + {${p.b}} + {${p.c}}`,
  },
  {
    id: 'diff',
    label: 'Difference  (A − B)',
    params: [{ k: 'a', lbl: 'A  (from)' }, { k: 'b', lbl: 'B  (subtract)' }],
    build: p => `{${p.a}} - {${p.b}}`,
  },
  {
    id: 'ratio',
    label: 'Ratio / Margin  (A ÷ B)',
    params: [{ k: 'a', lbl: 'Numerator A' }, { k: 'b', lbl: 'Denominator B' }],
    build: p => `{${p.a}} / {${p.b}}`,
  },
  {
    id: 'gross_margin',
    label: 'Gross Margin  ((A − B) ÷ A)',
    params: [{ k: 'a', lbl: 'Revenue A' }, { k: 'b', lbl: 'Cost B' }],
    build: p => `({${p.a}} - {${p.b}}) / {${p.a}}`,
  },
  {
    id: 'yoy',
    label: 'YoY % Change  (auto prior period)',
    params: [{ k: 'ref', lbl: 'Row' }],
    build: p => `({${p.ref}} - {PREV:${p.ref}}) / ABS({PREV:${p.ref}})`,
  },
  {
    id: 'pct_of',
    label: '% of Total  (A ÷ Total)',
    params: [{ k: 'a', lbl: 'Value A' }, { k: 'total', lbl: 'Total (denominator)' }],
    build: p => `{${p.a}} / {${p.total}}`,
  },
  {
    id: 'ebitda',
    label: 'EBITDA  (Op. Income + D&A)',
    params: [{ k: 'opinc', lbl: 'Operating Income' }, { k: 'da', lbl: 'D&A' }],
    build: p => `{${p.opinc}} + {${p.da}}`,
  },
  {
    id: 'yearsum',
    label: 'Fiscal Year Total  (sum all quarters → annual)',
    params: [{ k: 'ref', lbl: 'Row / Metric' }],
    build: p => `{YEARSUM:${p.ref}}`,
  },
  {
    id: 'yearavg',
    label: 'Fiscal Year Average  (average all quarters → annual)',
    params: [{ k: 'ref', lbl: 'Row / Metric' }],
    build: p => `{YEARAVG:${p.ref}}`,
  },
];

/**
 * Inline picker for pre-canned formula patterns.
 * Lets the user choose a formula type and assign row references to each slot.
 */
function CannedFormulaBuilder({ allRows, onApply, onCancel }) {
  const labelMap = React.useMemo(() => {
    const m = {};
    for (const row of (allRows || [])) {
      if (row.canonical_key) m[row.canonical_key] = row.label || row.canonical_key;
    }
    return m;
  }, [allRows]);

  const keys = Object.keys(labelMap);
  const [formulaIdx, setFormulaIdx] = useState(0);
  const formula = CANNED_FORMULAS[formulaIdx];
  const [params, setParams] = useState(() =>
    Object.fromEntries(formula.params.map((p, i) => [p.k, keys[i] || keys[0] || '']))
  );

  useEffect(() => {
    setParams(Object.fromEntries(
      CANNED_FORMULAS[formulaIdx].params.map((p, i) => [p.k, keys[i] || keys[0] || ''])
    ));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formulaIdx]);

  const setParam = (k, v) => setParams(prev => ({ ...prev, [k]: v }));
  const canApply = keys.length > 0 && formula.params.every(p => params[p.k]);

  return (
    <div style={{ border: '1px solid #84b6f4', borderRadius: 6, padding: '6px 8px', background: '#f0f6ff', marginTop: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 10, color: '#084298', fontWeight: 600 }}>Formula type:</span>
        <select
          value={formulaIdx}
          onChange={e => setFormulaIdx(Number(e.target.value))}
          style={{ fontSize: 11, flex: 1, padding: '2px 4px', borderRadius: 4, border: '1px solid #84b6f4' }}
        >
          {CANNED_FORMULAS.map((f, i) => <option key={f.id} value={i}>{f.label}</option>)}
        </select>
      </div>

      {formula.params.map(p => (
        <div key={p.k} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
          <span style={{ fontSize: 10, color: '#6c757d', width: 110, flexShrink: 0, textAlign: 'right' }}>{p.lbl}:</span>
          <select
            value={params[p.k] || ''}
            onChange={e => setParam(p.k, e.target.value)}
            style={{ fontSize: 11, flex: 1, padding: '1px 4px', borderRadius: 4, border: '1px solid #ced4da', background: '#cfe2ff', color: '#084298' }}
          >
            {keys.map(k => <option key={k} value={k}>{labelMap[k]}</option>)}
            {params[p.k] && !keys.includes(params[p.k]) && (
              <option value={params[p.k]}>{params[p.k]}</option>
            )}
          </select>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 4, marginTop: 6, justifyContent: 'flex-end' }}>
        <button
          onClick={onCancel}
          style={{ fontSize: 10, padding: '2px 8px', border: '1px solid #ced4da', borderRadius: 3, background: 'transparent', color: '#6c757d', cursor: 'pointer' }}
        >Cancel</button>
        <button
          disabled={!canApply}
          onClick={() => onApply(formula.build(params))}
          style={{ fontSize: 10, padding: '2px 8px', border: 'none', borderRadius: 3, background: canApply ? '#0d6efd' : '#adb5bd', color: 'white', cursor: canApply ? 'pointer' : 'default' }}
        >Apply</button>
      </div>
    </div>
  );
}

/**
 * Structured editor for excel_formula_template strings.
 * Renders {canonical_key} tokens as labeled dropdown chips and
 * literal text segments (operators, function wrappers) as small inline inputs.
 */
function FormulaTemplateEditor({ template, allRows, onSave }) {
  const labelMap = React.useMemo(() => {
    const m = {};
    for (const row of (allRows || [])) {
      if (row.canonical_key) m[row.canonical_key] = row.label || row.canonical_key;
    }
    return m;
  }, [allRows]);

  const canonicalKeys = Object.keys(labelMap);

  const parseTokens = (tpl) => {
    if (!tpl) return [];
    const tokens = [];
    const re = /\{([^}]+)\}/g;
    let last = 0, m;
    while ((m = re.exec(tpl)) !== null) {
      if (m.index > last) tokens.push({ type: 'lit', val: tpl.slice(last, m.index) });
      tokens.push({ type: 'key', val: m[1] });
      last = m.index + m[0].length;
    }
    if (last < tpl.length) tokens.push({ type: 'lit', val: tpl.slice(last) });
    return tokens;
  };

  const buildTemplate = (toks) => toks.map(t => t.type === 'key' ? `{${t.val}}` : t.val).join('');

  const [tokens, setTokens] = useState(() => parseTokens(template || ''));
  const lastSaved = useRef(template || '');
  const [rawMode, setRawMode] = useState(false);
  const [rawText, setRawText] = useState(template || '');
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if ((template || '') !== lastSaved.current) {
      lastSaved.current = template || '';
      setTokens(parseTokens(template || ''));
      setRawText(template || '');
    }
  }, [template]);

  const commit = (newTokens) => {
    const tpl = buildTemplate(newTokens);
    lastSaved.current = tpl;
    setTokens(newTokens);
    setRawText(tpl);
    onSave(tpl);
  };

  const commitRaw = (tpl) => {
    lastSaved.current = tpl;
    setTokens(parseTokens(tpl));
    setRawText(tpl);
    onSave(tpl);
  };

  const toggleRaw = () => {
    if (rawMode) {
      // leaving raw mode: re-parse whatever is in the text box
      commitRaw(rawText);
    }
    setRawMode(r => !r);
  };

  const changeKey = (i, newKey) => {
    const t = [...tokens];
    t[i] = { type: 'key', val: newKey };
    commit(t);
  };

  const changeLit = (i, newVal) => {
    const t = [...tokens];
    t[i] = { type: 'lit', val: newVal };
    commit(t);
  };

  const removeToken = (i) => commit(tokens.filter((_, j) => j !== i));

  const addKey = () => {
    const defaultKey = canonicalKeys[0] || 'revenue';
    const sep = tokens.length > 0 ? { type: 'lit', val: ' + ' } : null;
    commit(sep ? [...tokens, sep, { type: 'key', val: defaultKey }] : [{ type: 'key', val: defaultKey }]);
  };

  if (canonicalKeys.length === 0) return null;

  const editBtn = (
    <button
      onClick={toggleRaw}
      title={rawMode ? 'Switch to visual editor' : 'Edit raw formula text'}
      style={{ background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer', color: rawMode ? '#0d6efd' : '#adb5bd', fontSize: 12, lineHeight: 1, flexShrink: 0 }}
    >
      <i className={rawMode ? 'bi bi-check2' : 'bi bi-pencil'} />
    </button>
  );

  if (rawMode) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          type="text"
          autoFocus
          value={rawText}
          onChange={e => setRawText(e.target.value)}
          onBlur={() => { commitRaw(rawText); setRawMode(false); }}
          onKeyDown={e => { if (e.key === 'Enter') { commitRaw(rawText); setRawMode(false); } if (e.key === 'Escape') setRawMode(false); }}
          style={{
            flexGrow: 1, fontSize: 11, fontFamily: 'monospace', padding: '2px 4px',
            border: '1px solid #84b6f4', borderRadius: 4, background: '#f8f9fa', color: '#212529', outline: 'none',
          }}
          placeholder="e.g. {revenue} - {cost_of_revenue}  or cross-sheet: {IS.revenue}"
        />
        {editBtn}
      </div>
    );
  }

  const pickerBtn = (
    <button
      onClick={() => setPickerOpen(o => !o)}
      title="Pick a formula template"
      style={{ background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer', color: pickerOpen ? '#0d6efd' : '#adb5bd', fontSize: 12, lineHeight: 1, flexShrink: 0 }}
    >
      <i className="bi bi-calculator" />
    </button>
  );

  // Empty state — show prompts for raw or canned picker
  if (tokens.length === 0 && !pickerOpen) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          onClick={() => setRawMode(true)}
          style={{
            background: 'none', border: '1px dashed #dee2e6', borderRadius: 4,
            padding: '2px 8px', cursor: 'pointer', color: '#adb5bd', fontSize: 11,
            fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 4,
          }}
          title="Set Excel formula template"
        >
          <i className="bi bi-code-slash" style={{ fontSize: 10 }} />
          Set Excel formula…
        </button>
        {pickerBtn}
      </div>
    );
  }

  if (pickerOpen) {
    return (
      <CannedFormulaBuilder
        allRows={allRows}
        onApply={(tpl) => { commitRaw(tpl); setPickerOpen(false); }}
        onCancel={() => setPickerOpen(false)}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, fontFamily: 'monospace', fontSize: 11, minHeight: 24 }}>
      {tokens.map((tok, i) =>
        tok.type === 'key' ? (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <select
              value={tok.val}
              onChange={e => changeKey(i, e.target.value)}
              style={{
                fontSize: 11, fontFamily: 'monospace', padding: '1px 4px',
                borderRadius: 4, background: '#cfe2ff', border: '1px solid #84b6f4',
                color: '#084298', cursor: 'pointer',
              }}
            >
              {canonicalKeys.map(k => <option key={k} value={k}>{labelMap[k]}</option>)}
              {!canonicalKeys.includes(tok.val) && <option value={tok.val}>{tok.val}</option>}
            </select>
            <button
              onClick={() => removeToken(i)}
              style={{ background: 'none', border: 'none', padding: '0 1px', cursor: 'pointer', color: '#adb5bd', fontSize: 13, lineHeight: 1 }}
              title="Remove"
            >×</button>
          </span>
        ) : (
          <input
            key={i}
            type="text"
            value={tok.val}
            onChange={e => changeLit(i, e.target.value)}
            style={{
              fontSize: 11, fontFamily: 'monospace', padding: '1px 0',
              width: Math.max(16, tok.val.length * 7) + 'px',
              border: 'none', borderBottom: '1px dashed #dee2e6',
              background: 'transparent', color: '#6c757d', outline: 'none',
            }}
          />
        )
      )}
      <button
        onClick={addKey}
        style={{
          fontSize: 10, padding: '1px 6px', border: '1px dashed #adb5bd',
          borderRadius: 3, background: 'transparent', color: '#6c757d', cursor: 'pointer',
        }}
        title="Add metric reference"
      >+ key</button>
      {pickerBtn}
      {editBtn}
    </div>
  );
}

/** Editor for combined-row component lists (list of canonical_keys summed together). */
function ComponentEditor({ components, allRows, onUpdate }) {
  const [adding, setAdding] = useState(false);

  const labelMap = React.useMemo(() => {
    const m = {};
    for (const row of (allRows || [])) {
      if (row.canonical_key) m[row.canonical_key] = row.label || row.canonical_key;
    }
    return m;
  }, [allRows]);

  const candidateKeys = Object.keys(labelMap).filter(k => !components.includes(k));

  const remove = (idx) => onUpdate(components.filter((_, i) => i !== idx));
  const add = (key) => {
    const k = key.trim();
    if (k && !components.includes(k)) onUpdate([...components, k]);
    setAdding(false);
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
      <i className="bi bi-plus-circle me-1" style={{ color: '#0a3622', fontSize: 11 }} />
      {components.map((k, i) => (
        <React.Fragment key={k}>
          {i > 0 && <span style={{ fontSize: 11, color: '#6c757d' }}>+</span>}
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            backgroundColor: '#d1e7dd', border: '1px solid #a3cfbb', borderRadius: 4,
            padding: '1px 6px', fontSize: 11, fontFamily: 'monospace', color: '#0a3622',
          }}>
            <span title={k}>{labelMap[k] || k}</span>
            <button
              onClick={() => remove(i)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6c757d', lineHeight: 1 }}
              title="Remove"
            >×</button>
          </span>
        </React.Fragment>
      ))}
      {!adding && (
        <button
          className="btn btn-link btn-sm p-0"
          style={{ fontSize: 11 }}
          onClick={() => setAdding(true)}
          title="Add a row reference to this combined row"
        >
          + add row
        </button>
      )}
      {adding && (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <select
            autoFocus
            size={1}
            onChange={e => add(e.target.value)}
            onBlur={() => setAdding(false)}
            defaultValue=""
            style={{ fontSize: 11, fontFamily: 'monospace', padding: '2px 4px', border: '1px solid #0d6efd', borderRadius: 4, maxWidth: 240 }}
          >
            <option value="" disabled>Select a row…</option>
            {candidateKeys.map(k => (
              <option key={k} value={k}>{labelMap[k]} ({k})</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

function TagEditor({ tags, onUpdate, readOnly, knownTags = [] }) {
  const [adding, setAdding] = useState(false);

  const remove = (idx) => onUpdate(tags.filter((_, i) => i !== idx));
  const add = (tag) => {
    const t = tag.trim();
    if (t && !tags.includes(t)) onUpdate([...tags, t]);
    setAdding(false);
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
      {tags.map((t, i) => (
        <TagChip key={t} tag={t} onRemove={readOnly ? null : () => remove(i)} />
      ))}
      {!readOnly && !adding && (
        <button
          className="btn btn-link btn-sm p-0"
          style={{ fontSize: 11 }}
          onClick={() => setAdding(true)}
        >
          + add tag
        </button>
      )}
      {adding && (
        <TagPicker
          knownTags={knownTags}
          excludeTags={tags}
          onPick={add}
          onCancel={() => setAdding(false)}
        />
      )}
      {tags.length === 0 && !adding && !readOnly && (
        <span style={{ fontSize: 11, color: '#adb5bd', fontStyle: 'italic' }}>no tags — not sourced from SEC</span>
      )}
      {tags.length === 0 && readOnly && (
        <span style={{ fontSize: 11, color: '#adb5bd', fontStyle: 'italic' }}>—</span>
      )}
    </div>
  );
}

/**
 * Editor for metric rows that supports tag-group resolution:
 *   - Groups are tried in order (top to bottom).
 *   - A single-tag group uses "first found" semantics (existing behaviour).
 *   - A multi-tag group sums every tag found in the filing.
 *   - If a group yields no value, the next group is tried.
 *
 * Receives tagGroups: [[tag, ...], ...] — at least one group (possibly empty).
 * Calls onUpdate(newGroups) on every edit; parent is responsible for
 * syncing both `sec_tag_groups` and the flat `sec_tags` on the row.
 */
function MetricTagGroupsEditor({ tagGroups, knownTags, onUpdate }) {
  const [addingFor, setAddingFor] = useState(null); // groupIdx where picker is open

  const allTagsFlat = tagGroups.flat();
  const hasMultipleGroups = tagGroups.length > 1;
  const hasAnySumGroup = tagGroups.some(g => g.length > 1);

  const removeTag = (gIdx, tIdx) => {
    const newGroups = tagGroups
      .map((g, i) => i === gIdx ? g.filter((_, j) => j !== tIdx) : g)
      .filter(g => g.length > 0);
    onUpdate(newGroups.length ? newGroups : [[]]);
  };

  const addTag = (gIdx, tag) => {
    const t = tag.trim();
    if (!t) return;
    const newGroups = tagGroups.map((g, i) =>
      i === gIdx && !g.includes(t) ? [...g, t] : g
    );
    onUpdate(newGroups);
    setAddingFor(null);
  };

  const handleCancelPicker = (gIdx) => {
    // If the group the picker was open for is empty, remove it
    if (tagGroups[gIdx] && tagGroups[gIdx].length === 0 && tagGroups.length > 1) {
      onUpdate(tagGroups.filter((_, i) => i !== gIdx));
    }
    setAddingFor(null);
  };

  const addGroup = () => {
    const newGroups = [...tagGroups, []];
    onUpdate(newGroups);
    setAddingFor(newGroups.length - 1);
  };

  const removeGroup = (gIdx) => {
    const newGroups = tagGroups.filter((_, i) => i !== gIdx);
    onUpdate(newGroups.length ? newGroups : [[]]);
    if (addingFor === gIdx) setAddingFor(null);
  };

  return (
    <div>
      {tagGroups.map((group, gIdx) => (
        <React.Fragment key={gIdx}>
          {gIdx > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '3px 0' }}>
              <div style={{ flex: 1, height: 1, background: '#dee2e6' }} />
              <span style={{ fontSize: 9, color: '#adb5bd', fontWeight: 700, letterSpacing: '0.08em' }}>OR</span>
              <div style={{ flex: 1, height: 1, background: '#dee2e6' }} />
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            {group.map((tag, tIdx) => (
              <React.Fragment key={tag}>
                {tIdx > 0 && (
                  <span style={{ fontSize: 11, color: '#0a3622', fontWeight: 700, padding: '0 1px' }}>+</span>
                )}
                <TagChip tag={tag} onRemove={() => removeTag(gIdx, tIdx)} />
              </React.Fragment>
            ))}
            {addingFor === gIdx ? (
              <TagPicker
                knownTags={knownTags}
                excludeTags={allTagsFlat}
                onPick={(tag) => addTag(gIdx, tag)}
                onCancel={() => handleCancelPicker(gIdx)}
              />
            ) : (
              <button
                className="btn btn-link btn-sm p-0"
                style={{ fontSize: 11 }}
                onClick={() => setAddingFor(gIdx)}
              >+ add tag</button>
            )}
            {hasMultipleGroups && addingFor !== gIdx && (
              <button
                onClick={() => removeGroup(gIdx)}
                title="Remove this fallback group"
                style={{ background: 'none', border: 'none', padding: '0 3px', cursor: 'pointer', color: '#adb5bd', fontSize: 14, lineHeight: 1, marginLeft: 2 }}
              >×</button>
            )}
          </div>
        </React.Fragment>
      ))}
      {addingFor === null && (
        <div style={{ marginTop: 2 }}>
          <button
            className="btn btn-link btn-sm p-0"
            style={{ fontSize: 10, color: '#6c757d' }}
            onClick={addGroup}
          >+ add fallback group</button>
        </div>
      )}
      {(hasMultipleGroups || hasAnySumGroup) && (
        <div style={{ fontSize: 10, color: '#adb5bd', marginTop: 3 }}>
          <i className="bi bi-diagram-3 me-1" />
          First group that yields a value is used; multi-tag groups are summed.
        </div>
      )}
      {!hasMultipleGroups && !hasAnySumGroup && allTagsFlat.length > 1 && (
        <div style={{ fontSize: 10, color: '#adb5bd', marginTop: 3 }}>
          <i className="bi bi-filter me-1" />Checked left-to-right — first tag found in the filing is used
        </div>
      )}
    </div>
  );
}

const HIGHLIGHT_COLORS = [
  { label: 'None', value: '' },
  { label: 'Yellow', value: 'FFF2CC' },
  { label: 'Blue', value: 'DDEEFF' },
  { label: 'Green', value: 'E2EFDA' },
  { label: 'Gray', value: 'F2F2F2' },
  { label: 'Orange', value: 'FCE4D6' },
  { label: 'Pink', value: 'FCE4EC' },
];

function FormatControls({ bold, borderBelow, highlight, formatType, rowMode, hideWhenEmpty, onChange }) {
  const [showPalette, setShowPalette] = useState(false);
  const paletteRef = useRef(null);

  useEffect(() => {
    if (!showPalette) return;
    const handler = (e) => {
      if (paletteRef.current && !paletteRef.current.contains(e.target)) setShowPalette(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPalette]);

  const base = { border: '1px solid #dee2e6', borderRadius: 3, background: 'transparent', color: '#6c757d', cursor: 'pointer', padding: '1px 5px', fontSize: 11, lineHeight: 1.4, display: 'inline-flex', alignItems: 'center' };
  const active = { borderColor: '#0d6efd', background: '#e7f3ff', color: '#0d6efd' };

  const toggleFormatType = (type) => {
    onChange({ format_type: formatType === type ? '' : type });
  };

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
      <div style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
        <button style={{ ...base, ...(bold ? active : {}), fontWeight: 700 }} title="Bold" onClick={() => onChange({ bold: !bold })}>B</button>
        <button style={{ ...base, ...(borderBelow ? active : {}) }} title="Bottom border" onClick={() => onChange({ border_below: !borderBelow })}>
          <i className="bi bi-border-bottom" style={{ fontSize: 12 }} />
        </button>
        <div style={{ position: 'relative' }} ref={paletteRef}>
          <button
            style={{ ...base, ...(highlight ? active : {}), backgroundColor: highlight ? `#${highlight}` : 'transparent', minWidth: 20, justifyContent: 'center' }}
            title="Highlight color"
            onClick={() => setShowPalette(p => !p)}
          >
            {highlight ? '\u00a0' : <i className="bi bi-palette" style={{ fontSize: 11 }} />}
          </button>
          {showPalette && (
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1000, background: '#fff', border: '1px solid #ced4da', borderRadius: 4, padding: 6, display: 'flex', gap: 4, boxShadow: '0 2px 8px rgba(0,0,0,.15)' }}>
              {HIGHLIGHT_COLORS.map(c => (
                <button
                  key={c.value}
                  title={c.label}
                  onMouseDown={(e) => { e.preventDefault(); onChange({ highlight: c.value }); setShowPalette(false); }}
                  style={{ width: 20, height: 20, borderRadius: 3, cursor: 'pointer', border: `2px solid ${highlight === c.value ? '#0d6efd' : '#dee2e6'}`, background: c.value ? `#${c.value}` : 'white', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}
                >{!c.value && '✕'}</button>
              ))}
            </div>
          )}
        </div>
        <div style={{ width: 1, height: 14, background: '#dee2e6', margin: '0 1px' }} />
        <button style={{ ...base, ...(formatType === 'dollar' ? active : {}) }} title="Dollar format ($1,234)" onClick={() => toggleFormatType('dollar')}>$</button>
        <button style={{ ...base, ...(formatType === 'percent' ? active : {}) }} title="Percent format (12.3%)" onClick={() => toggleFormatType('percent')}>%</button>
        <button style={{ ...base, ...(formatType === 'cents' ? active : {}), fontSize: 10 }} title="Decimal dollar format ($1.23, for EPS)" onClick={() => toggleFormatType('cents')}>¢</button>
      </div>
      <RowVisibilityControls rowMode={rowMode} hideWhenEmpty={hideWhenEmpty} onChange={onChange} />
    </div>
  );
}

function AddRowForm({ existingKeys, onAdd, onCancel }) {
  const [label, setLabel] = useState('');
  const [rowType, setRowType] = useState('metric');
  const [canonicalKey, setCanonicalKey] = useState('');
  const [keyManual, setKeyManual] = useState(false);

  const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

  const derivedKey = keyManual ? canonicalKey : slug(label);
  const keyConflict = !!derivedKey && existingKeys.includes(derivedKey);

  const handleLabelChange = (val) => {
    setLabel(val);
    if (!keyManual) setCanonicalKey(slug(val));
  };

  const handleSubmit = () => {
    if (rowType === 'blank') {
      onAdd({
        label: '',
        canonical_key: `_blank_${Date.now()}`,
        row_type: 'blank',
        is_custom: true,
        sec_tags: [],
      });
      return;
    }
    if (!label.trim() || !derivedKey || keyConflict) return;
    const row = {
      label: label.trim(),
      canonical_key: derivedKey,
      row_type: rowType,
      is_custom: true,
      sec_tags: [],
      ...(rowType === 'formula' ? { formula_description: '', excel_formula_template: '' } : {}),
      ...(rowType === 'combined' ? { components: [] } : {}),
    };
    onAdd(row);
  };

  return (
    <tr style={{ backgroundColor: '#f0f7ff', borderTop: '2px solid #0d6efd' }}>
      <td />
      <td>
        <input
          autoFocus
          type="text"
          value={label}
          onChange={e => handleLabelChange(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); if (e.key === 'Escape') onCancel(); }}
          placeholder="Row label…"
          style={{ width: '100%', fontSize: 12, padding: '2px 4px', border: '1px solid #0d6efd', borderRadius: 4 }}
        />
        <div style={{ fontSize: 10, fontFamily: 'monospace', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: keyConflict ? '#dc3545' : '#adb5bd' }}>
            {keyConflict ? `"${derivedKey}" already exists` : (derivedKey || '…')}
          </span>
          <button
            onClick={() => { setKeyManual(m => !m); if (!keyManual) setCanonicalKey(derivedKey); }}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#0d6efd', fontSize: 9 }}
          >{keyManual ? '[auto]' : '[edit key]'}</button>
          {keyManual && (
            <input
              type="text"
              value={canonicalKey}
              onChange={e => setCanonicalKey(slug(e.target.value))}
              style={{ fontSize: 10, fontFamily: 'monospace', padding: '1px 3px', border: '1px solid #adb5bd', borderRadius: 3, width: 130 }}
            />
          )}
        </div>
      </td>
      <td>
        <select
          value={rowType}
          onChange={e => setRowType(e.target.value)}
          style={{ fontSize: 11, padding: '2px 2px', borderRadius: 4, border: '1px solid #ced4da', width: '100%' }}
        >
          <option value="metric">metric</option>
          <option value="formula">formula</option>
          <option value="combined">combined</option>
          <option value="header">header</option>
          <option value="blank">blank (spacer)</option>
        </select>
      </td>
      <td style={{ color: '#adb5bd', fontStyle: 'italic', fontSize: 11 }}>{rowType === 'blank' ? 'Adds an empty spacer row' : 'Fill in details after adding'}</td>
      <td /><td /><td />
      <td style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
            <button
              className="btn btn-primary btn-sm py-0 px-2"
              style={{ fontSize: 11 }}
              onClick={handleSubmit}
              disabled={rowType !== 'blank' && (!label.trim() || !derivedKey || keyConflict)}
            >Add</button>
          <button
            className="btn btn-outline-secondary btn-sm py-0 px-1"
            style={{ fontSize: 11 }}
            onClick={onCancel}
          >✕</button>
        </div>
      </td>
    </tr>
  );
}

function SheetTable({ rows, allRows, onRowChange, onReorder, onDelete, onAddRow, knownTags, focusedCanonicalKey }) {
  const dragIdx = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [adding, setAdding] = useState(false);

  const existingKeys = rows.map(r => r.canonical_key).filter(Boolean);

  const handleDragStart = (e, idx) => {
    dragIdx.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== dragOverIdx) setDragOverIdx(idx);
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    const from = dragIdx.current;
    if (from === null || from === idx) { setDragOverIdx(null); return; }
    const reordered = [...rows];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(idx, 0, moved);
    dragIdx.current = null;
    setDragOverIdx(null);
    onReorder(reordered);
  };

  const handleDragEnd = () => {
    dragIdx.current = null;
    setDragOverIdx(null);
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table table-sm table-hover align-middle" style={{ fontSize: 13, tableLayout: 'fixed', minWidth: 700 }}>
        <colgroup>
          <col style={{ width: '3%' }} />
          <col style={{ width: '21%' }} />
          <col style={{ width: '7%' }} />
          <col style={{ width: '47%' }} />
          <col style={{ width: '6%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '7%' }} />
        </colgroup>
        <thead className="table-light">
          <tr>
            <th></th>
            <th>Row Label</th>
            <th>Type</th>
            <th>SEC XBRL Tags (priority order)</th>
            <th style={{ textAlign: 'center' }}>Negate</th>
            <th style={{ textAlign: 'center' }}>Format</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            if (row.row_type === 'blank') {
              const isDragTarget = dragOverIdx === idx;
              return (
                <tr
                  key={idx}
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={(e) => handleDrop(e, idx)}
                  onDragEnd={handleDragEnd}
                  style={{
                    height: 18,
                    ...(isDragTarget ? { outline: '2px solid #0d6efd', outlineOffset: -2 } : {}),
                    cursor: 'grab',
                  }}
                >
                  <td style={{ textAlign: 'center', color: '#adb5bd', userSelect: 'none' }} title="Drag to reorder">
                    <i className="bi bi-grip-vertical" style={{ fontSize: 14 }} />
                  </td>
                  <td colSpan={5} style={{ borderBottom: '1px solid #f1f3f5' }} />
                  <td style={{ textAlign: 'center' }}>
                    <button
                      onClick={() => onDelete(idx)}
                      style={{ background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer', color: '#dc3545', fontSize: 13, lineHeight: 1, opacity: 0.6 }}
                      title="Remove row"
                    ><i className="bi bi-trash3" /></button>
                  </td>
                </tr>
              );
            }

            if (row.row_type === 'header') {
              const isDragTarget = dragOverIdx === idx;
              return (
                <tr
                  key={idx}
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={(e) => handleDrop(e, idx)}
                  onDragEnd={handleDragEnd}
                  style={{
                    backgroundColor: '#f5f5f5',
                    ...(isDragTarget ? { outline: '2px solid #0d6efd', outlineOffset: -2 } : {}),
                    cursor: 'grab',
                  }}
                >
                  <td style={{ textAlign: 'center', color: '#adb5bd', userSelect: 'none' }} title="Drag to reorder">
                    <i className="bi bi-grip-vertical" style={{ fontSize: 14 }} />
                  </td>
                  <td colSpan={4} style={{ fontStyle: 'italic', color: '#6c757d', fontSize: 11, padding: '4px 8px' }}>
                    {row.label}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <FormatControls
                      bold={!!row.bold}
                      borderBelow={!!row.border_below}
                      highlight={row.highlight || ''}
                      formatType={row.format_type || ''}
                      rowMode={row.row_mode || 'always'}
                      hideWhenEmpty={!!row.hide_when_empty}
                      onChange={(patch) => onRowChange(idx, patch)}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      onClick={() => onDelete(idx)}
                      style={{ background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer', color: '#dc3545', fontSize: 13, lineHeight: 1, opacity: 0.6 }}
                      title="Remove row"
                    ><i className="bi bi-trash3" /></button>
                  </td>
                </tr>
              );
            }

            const tagsReadOnly = row.row_type === 'formula' || row.row_type === 'combined';
            const negateDisabled = tagsReadOnly;
            const isDragTarget = dragOverIdx === idx;
            const isFocused = focusedCanonicalKey && row.canonical_key === focusedCanonicalKey;

            return (
              <tr
                key={idx}
                draggable
                data-canonical-key={row.canonical_key || undefined}
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDrop={(e) => handleDrop(e, idx)}
                onDragEnd={handleDragEnd}
                style={{
                  ...(isFocused ? { backgroundColor: '#e7f1ff', outline: '2px solid #0d6efd', outlineOffset: -2 } : {}),
                  ...(isDragTarget ? { outline: '2px solid #0d6efd', outlineOffset: -2 } : {}),
                  cursor: 'grab',
                }}
              >
                <td style={{ textAlign: 'center', color: '#adb5bd', userSelect: 'none' }} title="Drag to reorder">
                  <i className="bi bi-grip-vertical" style={{ fontSize: 14 }} />
                </td>
                <td>
                  <EditableLabel
                    value={row.label}
                    onSave={(v) => onRowChange(idx, { label: v })}
                  />
                  {row.canonical_key && (
                    <div style={{ fontSize: 10, color: '#adb5bd', fontFamily: 'monospace', marginTop: 1 }}>
                      {row.canonical_key}
                    </div>
                  )}
                </td>
                <td><Badge type={row.row_type} /></td>
                <td>
                  {row.row_type === 'formula' ? (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <i className="bi bi-calculator" style={{ color: '#856404', fontSize: 11, flexShrink: 0 }} />
                        <span style={{ color: '#856404', fontStyle: 'italic', fontSize: 11, flexGrow: 1 }}>
                          <EditableLabel
                            value={row.formula_description || '\u2014'}
                            onSave={(v) => onRowChange(idx, { formula_description: v })}
                          />
                        </span>
                      </div>
                      {'excel_formula_template' in row && (
                        <div style={{ marginTop: 4 }}>
                          {row.excel_formula_template ? (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                              <i className="bi bi-code-slash" style={{ color: '#0d6efd', fontSize: 10, flexShrink: 0, marginTop: 3 }} />
                              <div style={{ flexGrow: 1 }}>
                                <FormulaTemplateEditor
                                  template={row.excel_formula_template}
                                  allRows={allRows || rows}
                                  onSave={(v) => onRowChange(idx, { excel_formula_template: v })}
                                />
                              </div>
                            </div>
                          ) : (
                            <FormulaTemplateEditor
                              template=""
                              allRows={allRows || rows}
                              onSave={(v) => onRowChange(idx, { excel_formula_template: v })}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  ) : row.row_type === 'combined' && row.components ? (
                    <ComponentEditor
                      components={row.components}
                      allRows={allRows || rows}
                      onUpdate={(components) => onRowChange(idx, { components })}
                    />
                  ) : row.row_type === 'metric' ? (
                    <MetricTagGroupsEditor
                      tagGroups={
                        row.sec_tag_groups && row.sec_tag_groups.length > 0
                          ? row.sec_tag_groups
                          : (row.sec_tags || []).length > 0
                            ? (row.sec_tags || []).map(t => [t])
                            : [[]]
                      }
                      knownTags={knownTags}
                      onUpdate={(newGroups) => {
                        const flatTags = [...new Set(newGroups.flat())];
                        onRowChange(idx, { sec_tag_groups: newGroups, sec_tags: flatTags });
                      }}
                    />
                  ) : (
                    <TagEditor
                      tags={row.sec_tags || []}
                      readOnly={tagsReadOnly}
                      knownTags={knownTags}
                      onUpdate={(tags) => onRowChange(idx, { sec_tags: tags })}
                    />
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {!negateDisabled && (
                    <div className="form-check form-switch d-inline-flex justify-content-center m-0 p-0">
                      <input
                        className="form-check-input m-0"
                        type="checkbox"
                        checked={!!row.negate}
                        disabled={negateDisabled}
                        onChange={(e) => onRowChange(idx, { negate: e.target.checked })}
                        title={row.negate ? 'SEC value negated (outflow)' : 'SEC value used as-is'}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <FormatControls
                    bold={!!row.bold}
                    borderBelow={!!row.border_below}
                    highlight={row.highlight || ''}
                    formatType={row.format_type || ''}
                    rowMode={row.row_mode || 'always'}
                    hideWhenEmpty={!!row.hide_when_empty}
                    onChange={(patch) => onRowChange(idx, patch)}
                  />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    onClick={() => onDelete(idx)}
                    style={{ background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer', color: '#dc3545', fontSize: 13, lineHeight: 1, opacity: 0.5 }}
                    title="Remove row"
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0.5}
                  ><i className="bi bi-trash3" /></button>
                </td>
              </tr>
            );
          })}
          {adding && (
            <AddRowForm
              existingKeys={existingKeys}
              onAdd={(row) => { onAddRow(row); setAdding(false); }}
              onCancel={() => setAdding(false)}
            />
          )}
        </tbody>
      </table>
      {!adding && (
        <button
          className="btn btn-outline-primary btn-sm"
          style={{ fontSize: 12, marginTop: 4 }}
          onClick={() => setAdding(true)}
        >
          <i className="bi bi-plus-lg me-1" />Add row
        </button>
      )}
    </div>
  );
}


export default function ModelConfigTab({ focus, definitionId: propDefinitionId, onChangeDefinition }) {
      const [config, setConfig] = useState(null);   // { <sheetKey>: [rows...] }
      const [sheets, setSheets] = useState(DEFAULT_SHEETS);
      const [sheet, setSheet] = useState('IS');
      const [loading, setLoading] = useState(false);
      const [saving, setSaving] = useState(false);
      const [dirty, setDirty] = useState(false);
      const [error, setError] = useState(null);
      const [successMsg, setSuccessMsg] = useState(null);
      const [knownTags, setKnownTags] = useState([]);
      const [definitions, setDefinitions] = useState([{ id: 'default', name: 'Default' }]);
      const [focusedCanonicalKey, setFocusedCanonicalKey] = useState(null);
      const definitionId = propDefinitionId || focus?.definitionId || 'default';

  // Load known tags + definition list once
  useEffect(() => {
    researchService.getKnownTags().then(setKnownTags).catch(() => {});
    researchService.listModelDefinitions()
      .then(defs => { if (defs && defs.length) setDefinitions(defs); })
      .catch(() => {});
  }, []);


  // Load config for the active definition and apply focus if present.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await researchService.getGlobalModelConfig(definitionId);
        let cfg = data.config || {};
        const loadedSheets = (data.sheets && data.sheets.length) ? data.sheets : DEFAULT_SHEETS;

        let initialSheet = null;
        let stagedDirty = false;
        if (focus && focus.canonicalKey) {
          const { canonicalKey, addTag } = focus;
          let found = false;
          for (const s of loadedSheets) {
            const rows = cfg[s.key] || [];
            const i = rows.findIndex((r) => r.canonical_key === canonicalKey);
            if (i >= 0) {
              initialSheet = s.key;
              found = true;
              const existing = rows[i].sec_tags || [];
              if (addTag && !existing.includes(addTag)) {
                cfg = {
                  ...cfg,
                  [s.key]: rows.map((r, idx) =>
                    idx === i
                      ? { ...r, sec_tags: [...existing, addTag], is_modified: true }
                      : r
                  ),
                };
                stagedDirty = true;
              }
              break;
            }
          }
          if (!found) {
            setError(`Metric "${canonicalKey}" not found in this definition's sheets.`);
            setFocusedCanonicalKey(null);
          } else {
            setFocusedCanonicalKey(canonicalKey);
          }
        } else {
          // No focus: clear any focused canonical key
          setFocusedCanonicalKey(null);
        }

        if (cancelled) return;
        setConfig(cfg);
        setSheets(loadedSheets);
        setSheet(prev => {
          if (initialSheet) return initialSheet;
          // Always set to first available sheet if no focus or previous sheet not present
          return loadedSheets.some(s => s.key === prev) ? prev : loadedSheets[0]?.key || '';
        });
        setDirty(stagedDirty);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [definitionId, focus]);

  // Scroll the focused row into view once it renders on the active sheet.
  useEffect(() => {
    if (!focusedCanonicalKey || sheet == null) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`tr[data-canonical-key="${focusedCanonicalKey}"]`);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
    return () => clearTimeout(t);
  }, [focusedCanonicalKey, sheet, config]);

  const handleSelectDefinition = (newId) => {
    if (newId === definitionId) return;
    if (dirty && !window.confirm('Discard unsaved changes and switch definitions?')) return;
    if (onChangeDefinition) {
      onChangeDefinition(newId);
    }
  };

  const handleCreateDefinition = async (name, copyFrom) => {
    setSaving(true);
    setError(null);
    try {
      // copyFrom: definition id to clone, or '__blank__' for an empty
      // definition.  Defaults to the currently-active definition.
      const source = copyFrom || definitionId;
      const created = await researchService.createModelDefinition(name, source);
      const defs = await researchService.listModelDefinitions();
      setDefinitions(defs);
      // setDefinitionId is removed; parent must reload dialog with new definitionId if needed
      setSuccessMsg(`Created "${created.name}"`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDefinition = async () => {
    const current = definitions.find(d => d.id === definitionId);
    if (current?.is_default) {
      alert('Cannot delete the current default definition. Set another definition as default first.');
      return;
    }
    if (!window.confirm(`Delete spreadsheet definition "${current?.name || definitionId}"? This cannot be undone.`)) return;
    setSaving(true);
    setError(null);
    try {
      await researchService.deleteModelDefinition(definitionId);
      const defs = await researchService.listModelDefinitions();
      setDefinitions(defs);
      // setDefinitionId is removed; parent must reload dialog with new definitionId if needed
      setSuccessMsg(`Deleted "${current?.name || definitionId}"`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRenameDefinition = async (newName) => {
    setSaving(true);
    setError(null);
    try {
      await researchService.renameDefinition(definitionId, newName);
      const defs = await researchService.listModelDefinitions();
      setDefinitions(defs);
      setSuccessMsg(`Renamed to "${newName}"`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRowChange = (sheetKey, rowIdx, patch) => {
    setConfig(prev => {
      const updated = { ...prev };
      updated[sheetKey] = prev[sheetKey].map((row, i) =>
        i === rowIdx ? { ...row, ...patch, is_modified: true } : row
      );
      return updated;
    });
    setDirty(true);
  };

  const handleReorder = (sheetKey, newRows) => {
    setConfig(prev => ({ ...prev, [sheetKey]: newRows }));
    setDirty(true);
  };

  const handleDelete = (sheetKey, rowIdx) => {
    setConfig(prev => ({
      ...prev,
      [sheetKey]: prev[sheetKey].filter((_, i) => i !== rowIdx),
    }));
    setDirty(true);
  };

  const handleAddRow = (sheetKey, newRow) => {
    setConfig(prev => ({
      ...prev,
      [sheetKey]: [...prev[sheetKey], newRow],
    }));
    setDirty(true);
  };

  const handleSetPeriodicity = (sheetKey, value) => {
    setSheets(prev => prev.map(s => s.key === sheetKey ? { ...s, periodicity: value } : s));
    setDirty(true);
  };

  const handleAddSheet = (name) => {
    const existingKeys = new Set(sheets.map(s => s.key));
    let key = slugifySheetKey(name);
    let suffix = 2;
    while (existingKeys.has(key)) {
      key = `${slugifySheetKey(name)}_${suffix++}`;
    }
    setSheets(prev => [...prev, { key, name }]);
    setConfig(prev => ({ ...(prev || {}), [key]: [] }));
    setSheet(key);
    setDirty(true);
  };

  const handleRenameSheet = (key, newName) => {
    // Re-slug the key from the new name so renamed sheets become "custom"
    // sheets (the canonical IS/BS/CFS keys are the only ones that get
    // hardcoded default rows seeded on load).  Dedupe against existing keys.
    const existingKeys = new Set(sheets.map(s => s.key).filter(k => k !== key));
    let newKey = slugifySheetKey(newName);
    let suffix = 2;
    const base = newKey;
    while (existingKeys.has(newKey)) {
      newKey = `${base}_${suffix++}`;
    }
    setSheets(prev => prev.map(s => s.key === key ? { key: newKey, name: newName } : s));
    if (newKey !== key) {
      setConfig(prev => {
        if (!prev) return prev;
        const copy = { ...prev };
        copy[newKey] = copy[key] || [];
        delete copy[key];
        return copy;
      });
      setSheet(prev => prev === key ? newKey : prev);
    }
    setDirty(true);
  };

  const handleDeleteSheet = (key) => {
    if (sheets.length <= 1) {
      window.alert('A definition must have at least one sheet.');
      return;
    }
    const target = sheets.find(s => s.key === key);
    if (!window.confirm(`Remove sheet "${target?.name || key}" and all its rows from this definition?`)) return;
    setSheets(prev => prev.filter(s => s.key !== key));
    setConfig(prev => {
      const copy = { ...(prev || {}) };
      delete copy[key];
      return copy;
    });
    setSheet(prev => prev === key ? sheets.find(s => s.key !== key).key : prev);
    setDirty(true);
  };

  const rowPayload = (row) => ({
    canonical_key: row.canonical_key,
    label: row.label || '',
    row_type: row.row_type,
    sec_tags: row.sec_tags || [],
    ...(row.row_mode !== undefined ? { row_mode: row.row_mode } : {}),
    ...(row.hide_when_empty !== undefined ? { hide_when_empty: row.hide_when_empty } : {}),
    ...(row.sec_tag_groups !== undefined ? { sec_tag_groups: row.sec_tag_groups } : {}),
    ...(row.negate !== undefined ? { negate: row.negate } : {}),
    ...(row.formula_description !== undefined ? { formula_description: row.formula_description } : {}),
    ...(row.excel_formula_template !== undefined ? { excel_formula_template: row.excel_formula_template } : {}),
    ...(row.components !== undefined ? { components: row.components } : {}),
    ...(row.bold !== undefined ? { bold: row.bold } : {}),
    ...(row.border_below !== undefined ? { border_below: row.border_below } : {}),
    ...(row.highlight !== undefined ? { highlight: row.highlight || '' } : {}),
    ...(row.format_type !== undefined ? { format_type: row.format_type || '' } : {}),
  });

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const savedRows = {};
      for (const sheetDef of sheets) {
        const sheetKey = sheetDef.key;
        const sheetRows = config[sheetKey] || [];
        savedRows[sheetKey] = sheetRows
          .filter(r => r.canonical_key)
          .map(r => rowPayload(r));
      }
      await researchService.saveGlobalModelConfig(savedRows, definitionId, sheets);
      // Reload from backend to confirm what was persisted
      const saved = await researchService.getGlobalModelConfig(definitionId);
      setConfig(saved.config || {});
      if (saved.sheets && saved.sheets.length) setSheets(saved.sheets);
      setDirty(false);
      setSuccessMsg('Saved');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('Reset all overrides, remove all custom rows, and restore default sheets?')) return;
    setSaving(true);
    try {
      // Send empty saved_rows so backend falls back to built-in defaults on next load.
      await researchService.saveGlobalModelConfig({}, definitionId, DEFAULT_SHEETS);
      // Reload to confirm reset
      const reset = await researchService.getGlobalModelConfig(definitionId);
      setConfig(reset.config || {});
      if (reset.sheets && reset.sheets.length) setSheets(reset.sheets);
      setDirty(false);
      setSuccessMsg('All overrides cleared');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-muted py-4"><span className="spinner-border spinner-border-sm me-2" />Loading config…</div>;
  }

  return (
    <div>
      {/* Definition picker */}
      <div className="mb-3">
        <DefinitionSelector
          definitions={definitions}
          value={definitionId}
          onChange={handleSelectDefinition}
          onCreate={handleCreateDefinition}
          onRename={handleRenameDefinition}
          onDelete={handleDeleteDefinition}
          busy={loading || saving}
        />
      </div>

      {/* Toolbar */}
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <span className="text-muted" style={{ fontSize: 13 }}>
          Configure how each spreadsheet row is labelled and which SEC XBRL tags fill it.
        </span>
        <div className="ms-auto d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={handleResetAll} disabled={saving}>
            <i className="bi bi-arrow-counterclockwise me-1" />Reset all
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-floppy me-1" />}
            Save changes
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>
          {error}
          <button className="btn-close btn-sm float-end" onClick={() => setError(null)} />
        </div>
      )}
      {successMsg && (
        <div className="alert alert-success py-2 mb-3" style={{ fontSize: 13 }}>{successMsg}</div>
      )}

      {dirty && (
        <div className="alert alert-warning py-2 mb-3" style={{ fontSize: 13 }}>
          <i className="bi bi-exclamation-triangle me-1" />
          Unsaved changes. Click <strong>Save changes</strong> to persist.
        </div>
      )}

      {/* Sheet tabs */}
      <SheetTabBar
        sheets={sheets}
        activeKey={sheet}
        markers={{}}
        onSelect={setSheet}
        onRename={handleRenameSheet}
        onAdd={handleAddSheet}
        onDelete={handleDeleteSheet}
      />

      <div className="border border-top-0 rounded-bottom p-2 bg-white">
        {!config ? (
          <p className="text-muted py-3 mb-0">No config data.</p>
        ) : (
          <>
            {/* Periodicity toggle — only for custom/configurable sheets */}
            {(() => {
              const LEGACY_KEYS = new Set(['IS', 'BS', 'CFS']);
              if (LEGACY_KEYS.has(sheet)) return null;
              const activePeriodicity = sheets.find(s => s.key === sheet)?.periodicity || 'quarterly';
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 11 }}>
                  <span style={{ color: '#6c757d' }}>Columns:</span>
                  {['quarterly', 'annual'].map(p => {
                    const active = activePeriodicity === p;
                    return (
                      <button
                        key={p}
                        onClick={() => handleSetPeriodicity(sheet, p)}
                        style={{
                          fontSize: 10, padding: '2px 10px',
                          border: `1px solid ${active ? '#0d6efd' : '#ced4da'}`,
                          borderRadius: 3,
                          background: active ? '#0d6efd' : 'transparent',
                          color: active ? 'white' : '#6c757d',
                          cursor: 'pointer',
                          fontWeight: active ? 600 : 400,
                        }}
                      >
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </button>
                    );
                  })}
                  {activePeriodicity === 'annual' && (
                    <span style={{ color: '#6c757d', fontSize: 10 }}>
                      — one column per fiscal year; cross-sheet refs auto-sum all quarters
                    </span>
                  )}
                </div>
              );
            })()}
            <SheetTable
              rows={config[sheet] || []}
              allRows={sheets.flatMap(s =>
                s.key === sheet
                  ? (config[s.key] || [])
                  : (config[s.key] || []).filter(r => r.canonical_key).map(r => ({
                      ...r,
                      canonical_key: `${s.key}.${r.canonical_key}`,
                      label: `${s.name}: ${r.label || r.canonical_key}`,
                    }))
              )}
              knownTags={knownTags}
              focusedCanonicalKey={focusedCanonicalKey}
              onRowChange={(idx, patch) => handleRowChange(sheet, idx, patch)}
              onReorder={(newRows) => handleReorder(sheet, newRows)}
              onDelete={(idx) => handleDelete(sheet, idx)}
              onAddRow={(newRow) => handleAddRow(sheet, newRow)}
            />
          </>
        )}
      </div>
    </div>
  );
}
